import { useEffect, useRef } from 'react'
import type { Chart, IndicatorCalcCallback, KLineData } from 'klinecharts'
import type { CandleData } from '../../bot/types/chartData'
import { indicatorByName } from './catalog'
import { applyIndicator, ensureIndicatorClone, mintEngineName } from './indicators'
import { getIndicator } from './types'
import type { SymbolSliceState } from './workbenchState'

/**
 * An indicator calc that also sees `lead` — the bars before the chart's first one — and hands
 * back only the chart's part of the result.
 *
 * The walkthrough's chart starts a couple of bars before the first drawing, so an MA(200) drawn
 * from its own candles alone would be missing for the first 200 of them. Computing over the
 * history and slicing it off again gives the chart exactly the values the canvas shows for the
 * same bars, with none of the history on screen.
 *
 * Joined only while the chart's first bar IS the window's first bar (`joinMs`): the lead was cut
 * to end right before it, so anything else would put a gap in the series. A chart whose head was
 * paged away has more than enough of its own bars behind the view anyway.
 */
export function withHistory<D>(
  calc: IndicatorCalcCallback<D>,
  lead: KLineData[],
  joinMs: number,
): IndicatorCalcCallback<D> {
  return (dataList, indicator) => {
    if (lead.length === 0 || dataList[0]?.timestamp !== joinMs) return calc(dataList, indicator)
    const result = calc(lead.concat(dataList), indicator)
    const tail = (r: D[]) => r.slice(lead.length)
    return result instanceof Promise ? result.then(tail) : tail(result)
  }
}

/** Module-level so the default does not change identity every render and re-run the effect. */
const NO_HISTORY: CandleData[] = []

const toKline = (c: CandleData): KLineData => ({
  timestamp: c.time * 1000,
  open: c.open,
  high: c.high,
  low: c.low,
  close: c.close,
  volume: c.volume,
})

/**
 * Recreate the workbench's indicators on the walkthrough's replay chart.
 *
 * Reuses the same three-step recipe as Workbench's own restore (mintEngineName →
 * ensureIndicatorClone → applyIndicator), in the same saved-array order, so duplicate
 * instances land on the same engine names they would on the canvas. Rebuilding it here
 * instead would let the two charts' indicator sets diverge for no reason.
 *
 * Then each one is warmed with `history` (see withHistory), joined at `joinTs`, the chart's first
 * bar in unix seconds.
 *
 * The instances are not tracked in React state: nothing in walkthrough mode can add, remove
 * or restyle an indicator, so there is nothing to re-render for. They die with the chart.
 */
export default function useWalkthroughIndicators(
  chart: Chart | null,
  indicators: SymbolSliceState['indicators'] | null,
  history: CandleData[] = NO_HISTORY,
  joinTs: number | null = null,
): void {
  // Applied engine names, so a re-run removes exactly what it created.
  const appliedRef = useRef<Array<{ engineName: string; paneId: string; bandIds: string[] }>>([])

  useEffect(() => {
    if (!chart || !indicators || indicators.length === 0) return undefined

    const applied: Array<{ engineName: string; paneId: string; bandIds: string[] }> = []

    for (const entry of indicators) {
      const option = indicatorByName(entry.name)
      if (!option) continue

      const engineName = mintEngineName(option.name, applied.map((i) => i.engineName))
      if (engineName !== option.name) {
        const src = applied.find((i) => i.engineName === option.name)
        if (!src || !ensureIndicatorClone(chart, src, engineName, option.name)) continue
      }

      try {
        const res = applyIndicator(chart, option, entry.settings, engineName)
        if (res) applied.push({ engineName, paneId: res.paneId, bandIds: res.bandIds })
      } catch {
        // One bad saved indicator must not take the whole walkthrough down.
      }
    }

    // After the loop, never inside it. A duplicate's template is cloned off the FIRST instance's
    // live calc (ensureIndicatorClone), and the template registry is global and outlives this
    // chart — cloning a calc that was already warmed would warm the duplicate twice here, and
    // carry this run's history onto the canvas's own duplicates afterwards.
    if (history.length > 0 && joinTs != null) {
      const lead = history.map(toKline)
      for (const inst of applied) {
        try {
          const live = getIndicator(chart, inst.paneId, inst.engineName)
          if (!live?.calc) continue
          chart.overrideIndicator(
            { name: inst.engineName, calc: withHistory(live.calc, lead, joinTs * 1000) },
            inst.paneId,
          )
        } catch {
          // A cold indicator is still an indicator.
        }
      }
    }

    appliedRef.current = applied

    return () => {
      for (const inst of applied) {
        try {
          for (const id of inst.bandIds) chart.removeOverlay(id)
          chart.removeIndicator(inst.paneId, inst.engineName)
        } catch {
          /* engine already disposed */
        }
      }
      appliedRef.current = []
    }
  }, [chart, indicators, history, joinTs])
}
